"use client";

import React from "react";
import Image, { StaticImageData } from "next/image";

//main icons for three products
import workManagementIcon from "@/assets/images/threeProducts/work-management.svg";
import aiAgentsIcon from "@/assets/images/threeProducts/ai-agents.svg";
import enterpriseSearchIcon from "@/assets/images/threeProducts/enterprise-search.svg";

// icons for work management features
import portfoliosIcon from "@/assets/images/threeProducts/workFeaturesIcon/portfolios.svg";
import docsIcon from "@/assets/images/threeProducts/workFeaturesIcon/docs.svg";
import timeIcon from "@/assets/images/threeProducts/workFeaturesIcon/time.svg";

// icons for ai agents features
import actionsIcon from "@/assets/images/threeProducts/aiAgentsIcon/actions.svg";
import workflowsIcon from "@/assets/images/threeProducts/aiAgentsIcon/workflows.svg";
import analyticsIcon from "@/assets/images/threeProducts/aiAgentsIcon/analytics.svg";

// icons for enterprise search features
import hybridSearchIcon from "@/assets/images/threeProducts/enterpriseSearchIcon/hybrid-search.svg";
import generativeIcon from "@/assets/images/threeProducts/enterpriseSearchIcon/generative.svg";
import permissionIcon from "@/assets/images/threeProducts/enterpriseSearchIcon/permission.svg";

// replacement logos for work management
import replace1work from "@/assets/images/threeProducts/workFeaturesIcon/replace1.svg";
import replace2work from "@/assets/images/threeProducts/workFeaturesIcon/replace2.svg";
import replace3work from "@/assets/images/threeProducts/workFeaturesIcon/replace3.svg";
import replace4work from "@/assets/images/threeProducts/workFeaturesIcon/replace4.svg";
import replace5work from "@/assets/images/threeProducts/workFeaturesIcon/replace5.svg";
import replace6work from "@/assets/images/threeProducts/workFeaturesIcon/replace6.svg";

// replacement logos for ai agents
import replace1ai from "@/assets/images/threeProducts/aiAgentsIcon/replace1.svg";
import replace2ai from "@/assets/images/threeProducts/aiAgentsIcon/replace2.svg";
import replace3ai from "@/assets/images/threeProducts/aiAgentsIcon/replace3.svg";
import replace4ai from "@/assets/images/threeProducts/aiAgentsIcon/replace4.svg";
import replace5ai from "@/assets/images/threeProducts/aiAgentsIcon/replace5.svg";

// replacement logos for enterprise search
import replace1es from "@/assets/images/threeProducts/enterpriseSearchIcon/replace1.svg";
import replace2es from "@/assets/images/threeProducts/enterpriseSearchIcon/replace2.svg";
import replace3es from "@/assets/images/threeProducts/enterpriseSearchIcon/replace3.svg";
import replace4es from "@/assets/images/threeProducts/enterpriseSearchIcon/replace4.svg";
import replace5es from "@/assets/images/threeProducts/enterpriseSearchIcon/replace5.svg";

// preview images for center card
import workPreview from "@/assets/images/threeProductsTab/work-preview.svg";
import aiPreview from "@/assets/images/threeProductsTab/ai-preview.svg";
import searchPreview from "@/assets/images/threeProductsTab/search-preview.svg";

// right-side person image
import person1 from "@/assets/images/threeProductsTab/person-1.svg";
import person2 from "@/assets/images/threeProductsTab/person-2.svg";
import person3 from "@/assets/images/threeProductsTab/person-3.svg";

import CommonSubtitle from "@/components/common/CommonSubtitle";
import CommonTitle from "@/components/common/CommonTitle";
import CustomButton from "@/components/common/CustomButton";
import krooloLogo from "@/assets/images/logo.svg";

interface FeatureItem {
  icon: StaticImageData;
  title: string;
}

type ProductKey = "work" | "ai" | "search";

interface ProductConfig {
  id: ProductKey;
  label: string;
  title: string;
  layer?: string;
  tagline?: string;
  description: string;
  icon: StaticImageData;
  features: FeatureItem[];
  replaces: StaticImageData[];
  replacesLabels?: string[];
  exploreUrl: string;
  videoUrl: string;
  previewImage: StaticImageData;
  personImage: StaticImageData;
}

const ThreeProductsTab = () => {
  const products: Record<ProductKey, ProductConfig> = {
    work: {
      id: "work",
      label: "Kroolo Work",
      title: "Kroolo Work",
      layer: "The Execution Layer",
      tagline: "Where teams plan, track, and deliver",
      description:
        "An AI-first work management suite. Embed AI directly into sprints and projects to automate the manual grind of project admin.",
      icon: workManagementIcon,
      features: [
        { icon: portfoliosIcon, title: "Projects, Sprints & OKRs" },
        { icon: docsIcon, title: "Docs & Collaboration" },
        { icon: timeIcon, title: "Smart Workflows" },
      ],
      replaces: [
        replace1work,
        replace2work,
        replace3work,
        replace4work,
        replace5work,
        replace6work,
      ],
      exploreUrl: "/work-management",
      videoUrl: "https://www.youtube.com/embed/yTM3v5m4upk?autoplay=1",
      previewImage: workPreview,
      personImage: person1,
    },
    search: {
      id: "search",
      label: "Kroolo Search",
      title: "Kroolo Search",
      layer: "The Intelligence Layer",
      tagline: "Where knowledge becomes actionable",
      description:
        "Don't just store data-talk to it. Search and chat across all your enterprise apps with context, history, and permission awareness.",
      icon: enterpriseSearchIcon,
      features: [
        { icon: hybridSearchIcon, title: "Universal search" },
        { icon: permissionIcon, title: "Permissions aware" },
        { icon: generativeIcon, title: "Action-oriented answers" },
      ],
      replaces: [replace1es, replace2es, replace3es, replace4es, replace5es],
      exploreUrl: "/ai/enterprise-search",
      videoUrl: "https://www.youtube.com/embed/rQckpCTjTpc?autoplay=1",
      previewImage: searchPreview,
      personImage: person3,
    },
    ai: {
      id: "ai",
      label: "Kroolo Agents",
      title: "Kroolo Agents",
      layer: "The Autonomy Layer",
      tagline: "Where digital workers execute the plan",
      description:
        "Move beyond tools that wait for input. Deploy autonomous agents to triage requests, draft docs, and run RCA workflows 24/7.",
      icon: aiAgentsIcon,
      features: [
        { icon: actionsIcon, title: "Find & Resolve" },
        { icon: workflowsIcon, title: "Auto-generated plans" },
        { icon: analyticsIcon, title: "Audit & controls" },
      ],
      replaces: [replace1ai, replace2ai, replace3ai, replace4ai, replace5ai],
      exploreUrl: "/ai/ai-agent",
      videoUrl: "https://www.youtube.com/embed/sIrmSQuMkk0?autoplay=1",
      previewImage: aiPreview,
      personImage: person2,
    },
  };

  const [activeKey, setActiveKey] = React.useState<ProductKey>("work");
  const activeProduct = products[activeKey];

  return (
    <section className="bg-white py-12 xl:py-20">
      <div className="container mx-auto px-4">
        <div className="mb-8 text-center xl:mb-10">
          <CommonTitle
            color="#202020"
            fontSizeClass="text-[24px] lg:text-[32px] mb-2 lg:mb-4"
            fontWeightClass="font-semibold"
            as="h2"
          >
            <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
              3 Powerful Products.
            </span>{" "}
            One Unified Platform.
          </CommonTitle>
          <CommonSubtitle
            color="#202020"
            fontSizeWeight="max-w-[900px] mx-auto text-center"
          >
            Kroolo is your one-stop-shop to securely deploy AI, and bring tools
            in a common-sense way to work faster, smarter, and more
            cost-effective way.
          </CommonSubtitle>
        </div>
        <div className="mb-10 flex justify-center">
          <div className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#781E8F] to-[#422CBB] p-1">
            {(
              [products.work, products.search, products.ai] as ProductConfig[]
            ).map((product) => {
              const isActive = activeKey === product.id;
              return (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => setActiveKey(product.id)}
                  className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium md:text-[16px] ${isActive
                      ? "bg-white text-[#202020] shadow-sm"
                      : "text-white hover:text-white"
                    }`}
                >
                  <span
                    className={`hidden h-6 w-6 items-center justify-center rounded-full bg-white md:flex md:h-8 md:w-8 ${isActive ? "shadow-sm" : "opacity-80"
                      }`}
                  >
                    <Image
                      src={product.icon}
                      alt={`${product.label} icon`}
                      className="h-4 w-4 md:h-6 md:w-6"
                    />
                  </span>
                  <span>{product.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col xl:flex-row xl:items-stretch">
          <div className="w-full xl:w-[40%]">
            <div className="h-full rounded-l-3xl bg-[#F8F7FF] p-5 xl:p-7">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-fit items-center justify-center">
                  <Image
                    src={krooloLogo}
                    alt={activeProduct.title}
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
              {activeProduct.tagline && (
                <p className="mb-2 bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-[14px] font-medium text-transparent xl:text-[16px]">
                  "{activeProduct.tagline}"
                </p>
              )}
              <p className="mb-6 text-[14px] font-normal leading-relaxed text-[#202020]">
                {activeProduct.description}
              </p>
              <div className="mb-6 space-y-1">
                {activeProduct.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#f3f4fd]">
                      <Image
                        src={feature.icon}
                        alt={feature.title}
                        className="h-5 w-5"
                      />
                    </div>
                    <h3 className="text-[14px] font-normal text-[#202020]">
                      {feature.title}
                    </h3>
                  </div>
                ))}
              </div>
              <div className="mb-6 rounded-lg bg-white p-3">
                <p className="mb-2 text-xs tracking-[0.16em] text-[#6b6b6b]">
                  Replaces
                </p>
                {activeProduct.replaces.length ? (
                  <div className="flex flex-wrap items-center gap-4">
                    {activeProduct.replaces.map((logo, index) => (
                      <div
                        key={index}
                        className="flex h-8 w-8 items-center justify-center"
                      >
                        <Image
                          src={logo}
                          alt="Alternative product logo"
                          className="h-full w-full object-contain"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    {activeProduct.replaces.map((logo, index) => (
                      <div
                        key={index}
                        className="flex h-8 w-8 items-center justify-center"
                      >
                        <Image
                          src={logo}
                          alt="Alternative product logo"
                          className="h-5 w-5 object-contain"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <CustomButton
                  text={`Explore ${activeProduct.title}`}
                  url={activeProduct.exploreUrl}
                  variant="primary"
                  version="black"
                  target="none"
                />
                <CustomButton
                  text="Watch Video"
                  url={activeProduct.videoUrl}
                  variant="secondary"
                  version="black"
                  watchIcon
                />
              </div>
            </div>
          </div>
          <div className="hidden w-full flex-col gap-6 xl:flex xl:w-[40%] xl:flex-row">
            <div className="flex-1">
              <div className="flex h-full items-center justify-center">
                <div className="relative h-full w-full max-w-[520px] bg-[#EFEEFF] p-4">
                  <Image
                    src={activeProduct.previewImage}
                    alt={`${activeProduct.title} preview`}
                    className="h-full w-full object-cover"
                    priority
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="hidden w-full xl:flex xl:w-[20%]">
            <div className="relative h-[280px] w-full overflow-hidden rounded-r-3xl xl:h-full">
              <Image
                src={activeProduct.personImage}
                alt="Kroolo satisfied customer testimonial"
                className="h-full w-full object-cover"
                priority
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ThreeProductsTab;
